import _defineProperty from "/codebuild/output/src3618121175/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/defineProperty";
import { Keyframes, unit } from '@ant-design/cssinjs';
import { getStyle as getCheckboxStyle } from '../../checkbox/style';
import { genFocusOutline, resetComponent } from '../../style';
import { genCollapseMotion } from '../../style/motion';
import { genStyleHooks, mergeToken } from '../../theme/internal';
import { genDirectoryStyle } from './directory';
// ============================ Keyframes =============================
var treeNodeFX = new Keyframes('ant-tree-node-fx-do-not-use', {
  '0%': {
    opacity: 0
  },
  '100%': {
    opacity: 1
  }
});
// ============================== Switch ==============================
var getSwitchStyle = function getSwitchStyle(prefixCls, token) {
  return _defineProperty({}, ".".concat(prefixCls, "-switcher-icon"), {
    display: 'inline-block',
    fontSize: 10,
    verticalAlign: 'baseline',
    svg: {
      transition: "transform ".concat(token.motionDurationSlow)
    }
  });
};
// =============================== Drop ===============================
var getDropIndicatorStyle = function getDropIndicatorStyle(prefixCls, token) {
  return _defineProperty({}, ".".concat(prefixCls, "-drop-indicator"), {
    position: 'absolute',
    // it should displayed over the following node
    zIndex: 1,
    height: 2,
    backgroundColor: token.colorPrimary,
    borderRadius: 1,
    pointerEvents: 'none',
    '&:after': {
      position: 'absolute',
      top: -3,
      insetInlineStart: -6,
      width: 8,
      height: 8,
      backgroundColor: 'transparent',
      border: "".concat(unit(token.lineWidthBold), " solid ").concat(token.colorPrimary),
      borderRadius: '50%',
      content: '""'
    }
  });
};
export var genBaseStyle = function genBaseStyle(prefixCls, token) {
  var _Object$assign3;
  var treeCls = token.treeCls,
    treeNodeCls = token.treeNodeCls,
    treeNodePadding = token.treeNodePadding,
    titleHeight = token.titleHeight,
    indentSize = token.indentSize,
    nodeSelectedBg = token.nodeSelectedBg,
    nodeHoverBg = token.nodeHoverBg,
    colorTextQuaternary = token.colorTextQuaternary;
  return _defineProperty({}, treeCls, Object.assign(Object.assign({}, resetComponent(token)), (_Object$assign3 = {
    background: token.colorBgContainer,
    borderRadius: token.borderRadius,
    transition: "background-color ".concat(token.motionDurationSlow),
    '&-rtl': {
      direction: 'rtl'
    }
  }, _defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_Object$assign3, "&".concat(treeCls, "-rtl ").concat(treeCls, "-switcher_close ").concat(treeCls, "-switcher-icon svg"), {
    transform: 'rotate(90deg)'
  }), "&-focused:not(:hover):not(".concat(treeCls, "-active-focused)"), Object.assign({}, genFocusOutline(token))), "".concat(treeCls, "-list-holder-inner"), {
    alignItems: 'flex-start'
  }), "&".concat(treeCls, "-block-node"), _defineProperty({}, "".concat(treeCls, "-list-holder-inner"), _defineProperty(_defineProperty({
    alignItems: 'stretch'
  }, "".concat(treeCls, "-node-content-wrapper"), {
    flex: 'auto'
  }), "".concat(treeNodeCls, ".dragging:after"), {
    position: 'absolute',
    inset: 0,
    border: "1px solid ".concat(token.colorPrimary),
    opacity: 0,
    animationName: treeNodeFX,
    animationDuration: token.motionDurationSlow,
    animationPlayState: 'running',
    animationFillMode: 'forwards',
    content: '""',
    pointerEvents: 'none',
    borderRadius: token.borderRadius
  }))), treeNodeCls, _defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty({
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: treeNodePadding,
    lineHeight: unit(titleHeight),
    position: 'relative',
    // 非常重要，避免 drop-indicator 在拖拽过程中闪烁
    '&:before': {
      content: '""',
      position: 'absolute',
      zIndex: 1,
      insetInlineStart: 0,
      width: '100%',
      top: '100%',
      height: treeNodePadding
    }
  }, "&-disabled ".concat(treeCls, "-node-content-wrapper"), {
    color: token.colorTextDisabled,
    cursor: 'not-allowed',
    '&:hover': {
      background: 'transparent'
    }
  }), "&:not(".concat(treeNodeCls, "-disabled)"), _defineProperty({}, "".concat(treeCls, "-node-content-wrapper"), {
    '&:hover': {
      color: token.nodeHoverColor
    }
  })), "&-active ".concat(treeCls, "-node-content-wrapper"), {
    background: token.controlItemBgHover
  }), "&:not(".concat(treeNodeCls, "-disabled).filter-node ").concat(treeCls, "-title"), {
    color: token.colorPrimary,
    fontWeight: 500
  }), '&-draggable', _defineProperty(_defineProperty({
    cursor: 'grab'
  }, "".concat(treeCls, "-draggable-icon"), {
    // https://github.com/ant-design/ant-design/issues/41915
    flexShrink: 0,
    width: titleHeight,
    textAlign: 'center',
    visibility: 'visible',
    color: colorTextQuaternary
  }), "&".concat(treeNodeCls, "-disabled ").concat(treeCls, "-draggable-icon"), {
    visibility: 'hidden'
  }))), "".concat(treeCls, "-indent"), {
    alignSelf: 'stretch',
    whiteSpace: 'nowrap',
    userSelect: 'none',
    '&-unit': {
      display: 'inline-block',
      width: indentSize
    }
  }), "".concat(treeCls, "-draggable-icon"), {
    visibility: 'hidden'
  }), "".concat(treeCls, "-switcher, ").concat(treeCls, "-checkbox"), {
    marginInlineEnd: token.calc(token.calc(titleHeight).sub(token.controlInteractiveSize)).div(2).equal()
  }), "".concat(treeCls, "-switcher"), Object.assign(Object.assign({}, getSwitchStyle(prefixCls, token)), _defineProperty(_defineProperty(_defineProperty(_defineProperty({
    position: 'relative',
    flex: 'none',
    alignSelf: 'stretch',
    width: titleHeight,
    textAlign: 'center',
    cursor: 'pointer',
    userSelect: 'none',
    transition: "all ".concat(token.motionDurationSlow),
    '&-noop': {
      cursor: 'unset'
    },
    '&:before': {
      pointerEvents: 'none',
      content: '""',
      width: titleHeight,
      height: titleHeight,
      position: 'absolute',
      left: {
        _skip_check_: true,
        value: 0
      },
      top: 0,
      borderRadius: token.borderRadius,
      transition: "all ".concat(token.motionDurationSlow)
    }
  }, "&:not(".concat(treeCls, "-switcher-noop):hover:before"), {
    backgroundColor: token.colorBgTextHover
  }), "&_close ".concat(treeCls, "-switcher-icon svg"), {
    transform: 'rotate(-90deg)'
  }), '&-loading-icon', {
    color: token.colorPrimary
  }), '&-leaf-line', {
    position: 'relative',
    zIndex: 1,
    display: 'inline-block',
    width: '100%',
    height: '100%',
    // https://github.com/ant-design/ant-design/issues/31884
    '&:before': {
      position: 'absolute',
      top: 0,
      insetInlineEnd: token.calc(titleHeight).div(2).equal(),
      bottom: token.calc(treeNodePadding).mul(-1).equal(),
      marginInlineStart: -1,
      borderInlineEnd: "1px solid ".concat(token.colorBorder),
      content: '""'
    },
    '&:after': {
      position: 'absolute',
      width: token.calc(token.calc(titleHeight).div(2).equal()).mul(0.8).equal(),
      height: token.calc(titleHeight).div(2).equal(),
      borderBottom: "1px solid ".concat(token.colorBorder),
      content: '""'
    }
  }))), "".concat(treeCls, "-node-content-wrapper"), Object.assign(Object.assign({
    position: 'relative',
    minHeight: titleHeight,
    paddingBlock: 0,
    paddingInline: token.paddingXS,
    background: 'transparent',
    borderRadius: token.borderRadius,
    cursor: 'pointer',
    transition: "all ".concat(token.motionDurationMid, ", border 0s, line-height 0s, box-shadow 0s")
  }, getDropIndicatorStyle(prefixCls, token)), _defineProperty(_defineProperty({
    '&:hover': {
      backgroundColor: nodeHoverBg
    }
  }, "&".concat(treeCls, "-node-selected"), {
    color: token.nodeSelectedColor,
    backgroundColor: nodeSelectedBg
  }), "".concat(treeCls, "-iconEle"), {
    display: 'inline-block',
    width: titleHeight,
    height: titleHeight,
    textAlign: 'center',
    verticalAlign: 'top',
    '&:empty': {
      display: 'none'
    }
  }))), _defineProperty(_defineProperty(_defineProperty(_defineProperty(_Object$assign3, "".concat(treeCls, "-unselectable ").concat(treeCls, "-node-content-wrapper:hover"), {
    backgroundColor: 'transparent'
  }), "".concat(treeNodeCls, ".drop-container > [draggable]"), {
    boxShadow: "0 0 0 2px ".concat(token.colorPrimary)
  }), '&-show-line', _defineProperty(_defineProperty({}, "".concat(treeCls, "-indent-unit"), {
    position: 'relative',
    height: '100%',
    '&:before': {
      position: 'absolute',
      top: 0,
      insetInlineEnd: token.calc(titleHeight).div(2).equal(),
      bottom: token.calc(treeNodePadding).mul(-1).equal(),
      borderInlineEnd: "1px solid ".concat(token.colorBorder),
      content: '""'
    },
    '&-end:before': {
      display: 'none'
    }
  }), "".concat(treeCls, "-switcher"), {
    background: 'transparent',
    '&-line-icon': {
      // https://github.com/ant-design/ant-design/issues/32813
      verticalAlign: '-0.15em'
    }
  })), "".concat(treeNodeCls, "-leaf-last ").concat(treeCls, "-switcher-leaf-line:before"), {
    top: 'auto !important',
    bottom: 'auto !important',
    height: "".concat(unit(token.calc(titleHeight).div(2).equal()), " !important")
  }))));
};
// ============================== Merged ==============================
export var genTreeStyle = function genTreeStyle(prefixCls, token) {
  var treeCls = ".".concat(prefixCls);
  var treeNodeCls = "".concat(treeCls, "-treenode");
  var treeNodePadding = token.calc(token.paddingXS).div(2).equal();
  var treeToken = mergeToken(token, {
    treeCls: treeCls,
    treeNodeCls: treeNodeCls,
    treeNodePadding: treeNodePadding
  });
  return [
  // Basic
  genBaseStyle(prefixCls, treeToken),
  // Directory
  genDirectoryStyle(treeToken)];
};
export var initComponentToken = function initComponentToken(token) {
  var controlHeightSM = token.controlHeightSM,
    controlItemBgHover = token.controlItemBgHover,
    controlItemBgActive = token.controlItemBgActive;
  var titleHeight = controlHeightSM;
  return {
    titleHeight: titleHeight,
    indentSize: titleHeight,
    nodeHoverBg: controlItemBgHover,
    nodeHoverColor: token.colorText,
    nodeSelectedBg: controlItemBgActive,
    nodeSelectedColor: token.colorText
  };
};
export var prepareComponentToken = function prepareComponentToken(token) {
  var colorTextLightSolid = token.colorTextLightSolid,
    colorPrimary = token.colorPrimary;
  return Object.assign(Object.assign({}, initComponentToken(token)), {
    directoryNodeSelectedColor: colorTextLightSolid,
    directoryNodeSelectedBg: colorPrimary
  });
};
export default genStyleHooks('Tree', function (token, _ref) {
  var prefixCls = _ref.prefixCls;
  return [_defineProperty({}, token.componentCls, getCheckboxStyle("".concat(prefixCls, "-checkbox"), token)), genTreeStyle(prefixCls, token), genCollapseMotion(token)];
}, prepareComponentToken);